import React, { useState, useEffect, useRef } from "react";
import { FaChevronDown, FaSpinner } from "react-icons/fa";

const AutoCompleteDropdownStatic = ({
  options = null,
  placeholder = "Search...",
  value,
  onSelect,
  loading = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Handle Option Updates
  useEffect(() => {
    if (options) {
      setFilteredOptions(options);

      // If value exists and matches any option, set input text
      const selectedOption = options
        .flatMap((group) => group.items || [])
        .find((opt) => opt.value === value);
      if (selectedOption) setInputValue(selectedOption.label);
      else setInputValue("");
    }
  }, [options, value]);

  // Handle Input Change & Filter
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    if (value) {
      setDropdownOpen(true);

      if (options) {
        const newFilteredOptions = options
          .map((group) => ({
            ...group,
            items: group.items?.filter((item) =>
              item.label.toLowerCase().includes(value.toLowerCase())
            ),
          }))
          .filter((group) => group.items?.length > 0);
        setFilteredOptions(newFilteredOptions);
      }
    } else {
      setFilteredOptions(options);
      setDropdownOpen(false);
      onSelect(null);
    }
  };

  // Select an Option
  const handleSelect = (selectedItem) => {
    setInputValue(selectedItem.label);
    setDropdownOpen(false);
    onSelect(selectedItem.value);
  };

  // Close dropdown on outside click & reset input if no value is selected
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (!value) {
          setInputValue(""); // Clear input if no value selected
          setFilteredOptions(options || []); // Reset options
        } else {
          const selectedOption = options
            .flatMap((group) => group.items || [])
            .find((opt) => opt.value === value);
          if (selectedOption) setInputValue(selectedOption.label);
        }
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value, options]);

  return (
    <div className="autocomplete" ref={dropdownRef}>
      {/* Input Field */}
      <div className="input-wrapper" onClick={() => setDropdownOpen(true)}>
        <input
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setDropdownOpen(true)}
          disabled={loading} // Disable input during loading
        />
        {loading ? (
          <FaSpinner className="spinner" />
        ) : (
          <FaChevronDown className="dropdown-icon" />
        )}
      </div>

      {/* Dropdown List */}
      {isDropdownOpen && (
        <div className="dropdown">
          {loading ? (
            <div className="loading">Loading...</div>
          ) : options && options.length > 0 ? (
            filteredOptions.length > 0 ? (
              filteredOptions.map((group, index) => (
                <div key={index} className="group">
                  {group.label && (
                    <div className="group-label">{group.label}</div>
                  )}
                  {group.items?.map((item, idx) => (
                    <div
                      key={idx}
                      className="option"
                      onClick={() => handleSelect(item)}
                    >
                      {item.label}
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <div className="no-options">No matching results</div>
            )
          ) : (
            <div className="no-options">No options available</div>
          )}
        </div>
      )}
    </div>
  );
};

export default AutoCompleteDropdownStatic;
